import { Component, Inject, OnInit } from '@angular/core'
import {
    AbstractControl,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    ValidatorFn,
    Validators
} from '@angular/forms'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { CrudOps } from '../../../../api/constants/crud.enum'
import { TimesheetProject, TimesheetProjectMapping } from '../../../../api/models/timesheet-project.model'
import { TimesheetProjectService } from '../../../../api/services/timesheet-project.service'
import { LoaderService } from '../../../common/services/loader.service'
import { SnackbarService } from '../../../common/services/snackbar.service'
import { notUndefined } from '../../../common/utils/conditional-assert'
import { Utils } from '../../../common/utils/utils'
import { ClientContainer, TimesheetProjectContainer } from '../clients-and-projects.component'

export interface ProjectDialogData {
    crud: CrudOps
    project: TimesheetProject
    clients: Array<ClientContainer>
}

@Component({
    selector: 'ppw-app-project-dialog',
    templateUrl: './project-dialog.component.html',
    styleUrls: ['./project-dialog.component.scss']
})
export class ProjectDialogComponent implements OnInit {
    trackBy = Utils.trackByIndex
    dialogTitle?: string
    crud?: CrudOps
    project?: TimesheetProject
    projectFormGroup?: UntypedFormGroup
    clients!: Array<ClientContainer>
    filteredClients?: Array<ClientContainer>
    isClientsFound?: boolean
    selectedClient?: ClientContainer
    mappingInfo =
        'Mappings are used to link a worklog\n' +
        'with a project during JIRA import.\n' +
        'These mapping are listed hierarchically,\n' +
        "thus if corresponding 'Issue Key' is found,\n" +
        'it will disregard the lower ranked mappings.\n' +
        '\n' +
        "RANK 1. 'Issue Key' (unique key in DB)\n" +
        "RANK 2. 'Invoice ID' (unique key in DB)\n" +
        "RANK 3. 'Version ID' with 'Project Key' combination\n" +
        "RANK 4. 'Project Key'\n"

    clientControl?: UntypedFormControl

    private readonly prefix = 'ProjectDialogComponent'

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: ProjectDialogData,
        private readonly formBuilder: UntypedFormBuilder,
        readonly dialogRef: MatDialogRef<ProjectDialogComponent>,
        readonly loaderService: LoaderService,
        readonly timesheetProjectService: TimesheetProjectService,
        readonly snackbarService: SnackbarService
    ) {}

    ngOnInit(): void {
        this.crud = this.data.crud
        this.project = this.data.project
        this.clients = this.data.clients
        this.filteredClients = this.clients
        this.createProjectForm()
        this.crudHandler()
    }

    crudHandler(): void {
        switch (this.data.crud) {
            case CrudOps.Create:
                this.dialogTitle = 'New Project'
                break
            case CrudOps.Update:
                this.dialogTitle = 'Edit Projects'
                break
            case CrudOps.Delete:
                this.dialogTitle = 'Delete (= Archive) Project'
                break
            default:
                this.dialogTitle = ''
        }
    }

    createProjectForm(): void {
        const numericRegex = /^[Z0-9]+$/
        if (this.project) {
            this.clientControl = new UntypedFormControl(this.project.clientName ? this.project.clientName : '', [
                Validators.required,
                Validators.maxLength(40),
                Validators.minLength(1),
                this.legitimateClientValidator(this.clients)
            ])
            this.projectFormGroup = this.formBuilder.group({
                projectName: [
                    this.project.name ? this.project.name : '',
                    [Validators.required, Validators.maxLength(40), Validators.minLength(1)]
                ],
                clientName: this.clientControl,
                invoicedChecked: [this.project.toInvoice !== undefined ? this.project.toInvoice : true],
                detailedChecked: [this.project.detailedTimesheet !== undefined ? this.project.detailedTimesheet : true],
                sendToCostumerChecked: [this.project.sendToCustomer !== undefined ? this.project.sendToCustomer : true],
                showPriorityChecked: [this.project.showPriority !== undefined ? this.project.showPriority : false],
                mappingIssueKey: [this.project.mapping ? this.project.mapping.issueKey : null],
                mappingInvoiceId: [this.project.mapping ? this.project.mapping.invoiceId : null],
                mappingVersionId: [
                    this.project.mapping ? this.project.mapping.versionId : null,
                    [Validators.pattern(numericRegex)]
                ],
                mappingProjectKey: [this.project.mapping ? this.project.mapping.projectKey : null]
            })
        }
        if (this.crud === 4 && this.projectFormGroup) {
            this.projectFormGroup.disable()
        }
    }

    clientQuerySearch(input: string): void {
        this.filteredClients = this.clients
        this.filteredClients = this.clients.filter((client: ClientContainer | TimesheetProjectContainer) => {
            return client && client.name && client.name.toLowerCase().includes(input.toLowerCase())
        })
        this.isClientsFound = this.filteredClients.length !== 0
    }

    cancelDialog(): void {
        this.dialogRef.close()
    }

    setProject(): void {
        if (this.projectFormGroup && this.project && this.selectedClient) {
            const formValue = this.projectFormGroup.value
            this.project.name = formValue.projectName
            this.project.clientName = formValue.clientName
            this.project.toInvoice = formValue.invoicedChecked
            this.project.detailedTimesheet = formValue.detailedChecked
            this.project.sendToCustomer = formValue.sendToCostumerChecked
            this.project.showPriority = formValue.showPriorityChecked
            this.project.clientId = notUndefined(this.selectedClient.id)
            if (!this.project.mapping) {
                this.project.mapping = new TimesheetProjectMapping()
            }
            this.project.mapping.issueKey = formValue.mappingIssueKey !== null ? formValue.mappingIssueKey.trim() : null
            this.project.mapping.invoiceId =
                formValue.mappingInvoiceId !== null ? formValue.mappingInvoiceId.trim() : null
            this.project.mapping.versionId =
                formValue.mappingVersionId !== null ? formValue.mappingVersionId.trim() : null
            this.project.mapping.projectKey =
                formValue.mappingProjectKey !== null ? formValue.mappingProjectKey.trim() : null
        }
    }

    archiveProject(): void {
        this.loaderService.startLoading()
        this.setProject()
        if (this.project) {
            this.timesheetProjectService
                .archiveTimesheetProject(this.project)
                .then((archivedProject: TimesheetProject) => {
                    this.project = archivedProject
                    this.closeAndSendResponse()
                })
                .catch((err: any) => {
                    this.snackbarService.handleError(this.prefix, err, true)
                })
                .finally(() => {
                    this.loaderService.stopLoading()
                })
        }
    }

    saveProject(): void {
        this.loaderService.startLoading()
        this.setProject()
        if (this.project) {
            this.timesheetProjectService
                .saveTimesheetProject(this.project)
                .then((savedProject: TimesheetProject) => {
                    this.project = savedProject
                    this.closeAndSendResponse()
                })
                .catch((err: any) => {
                    this.snackbarService.handleError(this.prefix, err, true)
                })
                .finally(() => {
                    this.loaderService.stopLoading()
                })
        }
    }

    private closeAndSendResponse(): void {
        this.dialogRef.close({
            crud: this.crud,
            project: this.project
        })
    }

    private legitimateClientValidator(allClients: Array<ClientContainer>): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            if (control.value !== null && control.value !== undefined && allClients) {
                this.selectedClient = allClients.find((c) => c.name === control.value)

                return this.selectedClient === undefined ? { clientNotInList: true } : null
            }

            return null
        }
    }
}
